import { Cache } from '../cache/index';
import { defaultCacheTime } from '../constants-types/constants';
import { CacheSerializer } from './Cache.interface';
import { DefaultCacheSerializer } from './CacheSerializers';

/**
 * Decorator to return a promise resolving to a cached value if there's a cache
 * hit.
 *
 * @param getCache Function that returns the cache off the calling object
 * @param methodArgumentsToHashArgumentsFunction A function that takes in the
 * arguments of the calling function and returns the hashArguments param for the
 * cache.
 * @param methodArgumentsToClearCacheArgumentFunction A function that takes in
 * the arguments of the calling function and returns whether the cache should be
 * cleared before requesting the resource
 * Pass in null if there is no clearCache mechanism.
 * @param expirationTime The expiration time for the cache. Pass in -1 to never
 * expire.
 */
export function cacheable(
    getCache: () => Cache<any, any>,
    methodArgumentsToHashArgumentsFunction: Function,
    methodArgumentsToClearCacheArgumentFunction: Function | null = null,
    dataSerializer: CacheSerializer<any, any> = null,
    expirationTime: number = defaultCacheTime
) {
    return function (
        _target: any,
        _propertyKey: string,
        descriptor: PropertyDescriptor
    ) {
        const serializer = dataSerializer || new DefaultCacheSerializer();
        const fetchDataMethod = descriptor.value;
        descriptor.value = function (...args: any[]) {
            const cache = getCache.call(this);
            const hashArguments = methodArgumentsToHashArgumentsFunction(
                ...args
            );

            if (methodArgumentsToClearCacheArgumentFunction) {
                const clearCache: boolean =
                    methodArgumentsToClearCacheArgumentFunction(...args);
                if (clearCache) {
                    cache.clear(hashArguments);
                }
            }

            // Get the cached value
            const cacheHit = cache.get(hashArguments, expirationTime);
            // If it exists return the promise for the data
            if (cacheHit) {
                return cacheHit.then((data) => serializer.deserialize(data));
            }
            // Otherwise fetch the data as a promise
            const fetchDataPromise = fetchDataMethod.call(this, ...args);
            // Store the data serialized
            const fetchDataPromiseSerialized = fetchDataPromise.then(
                (data: any) => serializer.serialize(data)
            );
            cache.set(hashArguments, fetchDataPromiseSerialized);
            // Return the unserialized data
            return fetchDataPromise;
        };
    };
}

export function computeTruthyValueOnce() {
    return function (
        _target: any,
        _propertyKey: string,
        descriptor: PropertyDescriptor
    ) {
        let cachedValue: any;
        const originalFunction = descriptor.value;
        descriptor.value = function (...args: any[]) {
            if (cachedValue) {
                return cachedValue;
            }

            const functionResult = originalFunction.apply(this, args);
            if (functionResult) {
                cachedValue = functionResult;
            }
            return functionResult;
        };
    };
}
